import * as THREE from "three";
import { loader } from "@/utils/loader";
import { LightsManager } from "@/services/lighting";
import { eMatType, ASSET_PATHS } from "../../constants/materials";
import { Ao } from "@/services";
import { STORAGE_KEY_AO_ENABLED } from "@/constants";

const DEFAULT_PLATFORM_HEIGHT = 1.7;
const BACKGROUND_NAME = "background";

export default class Scene extends THREE.Scene {
  baseContainer = new THREE.Object3D();

  backgroundContainer = new THREE.Object3D();

  platformContainer = new THREE.Object3D();

  characterContainer = new THREE.Object3D();

  baseAndCharacterContainer = new THREE.Object3D();

  passes = [];

  screenshotPasses = [];

  lights: THREE.Light[] = [];
  private lightsManager: LightsManager;

  constructor() {
    super();
    this.lightsManager = LightsManager.getInstance();
  }

  async create(camera: THREE.PerspectiveCamera) {
    this.characterContainer.name = "characterContainer";

    this.baseContainer = new THREE.Object3D();
    this.baseContainer.name = "baseContainer";
    this.add(this.characterContainer);
    this.add(this.baseContainer);
    this.baseAndCharacterContainer.add(this.characterContainer);
    this.baseAndCharacterContainer.add(this.baseContainer);
    this.add(this.baseAndCharacterContainer);

    //await this.lightsManager.loadLightsFromJson(this);

    if (camera) {
      this.add(camera);
    }

    this.updateMatrixWorld();
  }

  loadPlatform = async () => {
    const platform = await loader.loadGLTF(
      ASSET_PATHS.platform,
      "platform",
      true,
      true
    );
    const ao = Ao.getInstance();
    // Ensure the platform can receive and cast shadows
    platform.traverse((child: THREE.Object3D) => {
      if ((child as any).isMesh) {
        (child as THREE.Mesh).receiveShadow = true;
        (child as THREE.Mesh).castShadow = false;
        if (ao?.materialSwitcher) {
          ao.setMaterialCorrespondance(child as THREE.Mesh);
        }
      }
    });
    platform.scale.setScalar(0.165);
    platform.updateMatrixWorld(true);
    this.platformContainer.name = "platform3D";
    this.platformContainer.position.y = DEFAULT_PLATFORM_HEIGHT;
    this.add(this.platformContainer);
    this.platformContainer.add(platform);
    // Use lower HDR intensity on platform so shadow contrast is visible
    const stored = localStorage.getItem(STORAGE_KEY_AO_ENABLED);
    const enabled = stored === "true";
    await this.lightsManager.loadHDR(platform, enabled ? 3 : 2);
    this.changeIntensity(this.lights[0], enabled ? 1.2 : 0.7);
  };

  setPlatformIntensity = (intensity: number) => {
    this.platformContainer.traverse((child: THREE.Object3D) => {
      if ((child as any).isMesh) {
        const mesh = child as THREE.Mesh;
        if (mesh.material instanceof THREE.MeshStandardMaterial) {
          mesh.material.envMapIntensity = intensity;
          mesh.material.needsUpdate = true;
        }
      }
    });
  };

  loadSkyBoxSphere = async (skyBox: string) => {
    const sphere = await loader.loadObj(
      ASSET_PATHS.spheretest,
      "sky",
      eMatType.BASIC,
      {},
      { map: skyBox },
      null,
      false
    );
    sphere.position.y = -100;
    sphere.rotation.y = Math.PI;
    sphere.scale.setScalar(0.1);
    sphere.updateMatrixWorld(true);
    this.backgroundContainer.name = BACKGROUND_NAME;
    this.backgroundContainer.add(sphere);
    this.add(this.backgroundContainer);
  };

  loadFBX = async (path: string, name: string) => {
    const fbx = await loader.loadFBX(path, name, true);
    //await this.lightsManager.loadHDR(fbx);
    return fbx;
  };

  loadGLTF = async (path: string, name: string) => {
    const gltf = await loader.loadGLTF(path, name, true, false);
    //await this.lightsManager.loadHDR(gltf);
    return gltf;
  };

  changeIntensity = (light: THREE.Light, intensity: number) => {
    light.intensity = intensity;
  };

  addToScene = (object: THREE.Object3D) => {
    this.characterContainer.add(object);
  };

  clearCharacterContainer = () => {
    this.characterContainer.clear();
  };

  clearBaseContainer = () => {
    this.baseContainer.clear();
  };

  removeFromScene = (object: THREE.Object3D) => {
    object.removeFromParent();
  };
}
